import { useState, useRef, useEffect } from "react";

const DEBUG_DEEPGRAM = false;
/** MediaRecorder timeslice < ~500ms often sends incomplete WebM clusters → Deepgram closes. */
const MEDIA_TIMESLICE_MS = 1000;
/** KeepAlive every 3–5s prevents NET-0001 when audio is quiet. */
const KEEPALIVE_MS = 3000;
const TOKEN_TIMEOUT_MS = 12000;

async function fetchWithTimeout(input, options = {}, timeoutMs = 30000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

function safeVibrate(pattern) {
  try {
    if ("vibrate" in navigator) navigator.vibrate(pattern);
  } catch (_e) {}
}

function pickMimeType() {
  if (typeof MediaRecorder === "undefined") return undefined;
  if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) return "audio/webm;codecs=opus";
  if (MediaRecorder.isTypeSupported("audio/webm")) return "audio/webm";
  return undefined;
}

/**
 * useVoiceToText Hook
 * Status states: 'idle' | 'connecting' | 'ready' | 'error'
 */
export const useVoiceToText = (onTranscript, onInterim, options = {}) => {
  const { language = "es" } = options;
  const languageRef = useRef(language);
  languageRef.current = language;

  const [isRecording, setIsRecording] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [voiceLevel, setVoiceLevel] = useState(0);
  const [lastSpeechAt, setLastSpeechAt] = useState(null);
  const [silenceMs, setSilenceMs] = useState(0);

  const SILENCE_THRESHOLD_MS = 3500;
  const isSilent = isRecording && typeof lastSpeechAt === "number" && silenceMs > SILENCE_THRESHOLD_MS;

  const wsRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const keepAliveRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const levelRafRef = useRef(null);
  const streamRef = useRef(null);
  const smoothedVoiceLevelRef = useRef(0);
  const lastLevelEmitRef = useRef(0);
  const debugLoggedRef = useRef(false);
  const intentionalCloseRef = useRef(false);
  const lastFinalEndMsRef = useRef(null);

  const stopLevelMonitor = () => {
    if (levelRafRef.current) {
      cancelAnimationFrame(levelRafRef.current);
      levelRafRef.current = null;
    }
    smoothedVoiceLevelRef.current = 0;
    lastLevelEmitRef.current = 0;
    setVoiceLevel(0);
  };

  const startLevelMonitor = (stream) => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.78;
      source.connect(analyser);
      analyserRef.current = analyser;

      const data = new Uint8Array(analyser.frequencyBinCount);
      const speechMinHz = 85;
      const speechMaxHz = 3400;
      const coreMinHz = 140;
      const coreMaxHz = 1200;
      const binHz = ctx.sampleRate / analyser.fftSize;

      const tick = (now = performance.now()) => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(data);

        let weightedSum = 0;
        let weightTotal = 0;

        for (let i = 0; i < data.length; i += 1) {
          const hz = i * binHz;
          if (hz < speechMinHz || hz > speechMaxHz) continue;
          const coreWeight = hz >= coreMinHz && hz <= coreMaxHz ? 1 : 0.42;
          const speechPresenceWeight = hz >= 300 && hz <= 2600 ? 0.78 : 0.5;
          const weight = coreWeight + speechPresenceWeight;
          weightedSum += data[i] * weight;
          weightTotal += weight;
        }

        const rawSpeechLevel = weightTotal ? weightedSum / weightTotal / 255 : 0;
        const noiseGate = rawSpeechLevel < 0.018 ? 0 : rawSpeechLevel;
        const boosted = Math.min(1, Math.max(0, (noiseGate - 0.018) * 5.2));
        const smoothed = smoothedVoiceLevelRef.current * 0.72 + boosted * 0.28;
        smoothedVoiceLevelRef.current = smoothed;

        if (now - lastLevelEmitRef.current > 33) {
          setVoiceLevel((current) => (Math.abs(current - smoothed) > 0.01 ? smoothed : current));
          lastLevelEmitRef.current = now;
        }

        levelRafRef.current = requestAnimationFrame(tick);
      };
      levelRafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      console.warn("[VOICE] Level monitor failed:", e);
    }
  };

  const playCues = (type) => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      if (type === "ready") {
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.1);
        gain.gain.setValueAtTime(0.1, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
        osc.start();
        osc.stop(ctx.currentTime + 0.2);
      } else if (type === "stop") {
        osc.frequency.setValueAtTime(440, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(220, ctx.currentTime + 0.1);
        gain.gain.setValueAtTime(0.1, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
        osc.start();
        osc.stop(ctx.currentTime + 0.2);
      } else if (type === "error") {
        osc.frequency.setValueAtTime(150, ctx.currentTime);
        osc.frequency.linearRampToValueAtTime(100, ctx.currentTime + 0.3);
        gain.gain.setValueAtTime(0.2, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        osc.start();
        osc.stop(ctx.currentTime + 0.3);
      }
    } catch (e) {
      console.warn("[VOICE] Could not play audio cue:", e);
    }
  };

  const cleanupMedia = () => {
    stopLevelMonitor();
    analyserRef.current = null;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try {
        mediaRecorderRef.current.stop();
      } catch (_e) {}
      mediaRecorderRef.current.stream?.getTracks().forEach((track) => track.stop());
    }
    mediaRecorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      setError(null);
      setStatus("connecting");
      intentionalCloseRef.current = false;
      lastFinalEndMsRef.current = null;
      safeVibrate(8);

      // Mic first — Deepgram closes ~10s with no audio; permission dialog must not block the socket.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;
      startLevelMonitor(stream);

      let key = "";
      const response = await fetchWithTimeout("/api/deepgram-token", {}, TOKEN_TIMEOUT_MS);
      if (response.ok) {
        const data = await response.json();
        key = data.key;
      }
      if (!key && process.env.REACT_APP_DEEPGRAM_API_KEY) {
        key = process.env.REACT_APP_DEEPGRAM_API_KEY;
      }
      if (!key) {
        cleanupMedia();
        throw new Error("No se pudo conectar con Deepgram.");
      }

      const cleanKey = key.trim().replace(/^["']|["']$/g, "");
      // Deepgram has no Latin model — `multi` is best-effort for LA toggle.
      const uiLang = languageRef.current || "es";
      const lang = uiLang === "la" ? "multi" : uiLang;
      const baseUrl = "wss://api.deepgram.com/v1/listen";
      const params = new URLSearchParams({
        model: "nova-2",
        language: lang,
        smart_format: "true",
        interim_results: "true",
        endpointing: "300",
      });
      const url = `${baseUrl}?${params.toString()}`;
      const startTime = Date.now();
      const ws = new WebSocket(url, ["token", cleanKey]);
      wsRef.current = ws;

      ws.onopen = () => {
        setStatus("ready");
        setIsRecording(true);
        setLastSpeechAt(Date.now());
        setSilenceMs(0);
        playCues("ready");
        safeVibrate([12, 25, 12]);

        keepAliveRef.current = setInterval(() => {
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: "KeepAlive" }));
          }
        }, KEEPALIVE_MS);

        const mimeType = pickMimeType();
        try {
          const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
          mediaRecorderRef.current = mediaRecorder;
          mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0 && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              wsRef.current.send(event.data);
            }
          };
          mediaRecorder.start(MEDIA_TIMESLICE_MS);
        } catch (recErr) {
          console.warn("[VOICE] MediaRecorder failed:", recErr);
          setError("No se pudo iniciar el grabador de audio.");
          setStatus("error");
          intentionalCloseRef.current = true;
          ws.close();
          cleanupMedia();
        }
      };

      ws.onmessage = (message) => {
        try {
          const data = JSON.parse(message.data);
          const alt = data.channel?.alternatives?.[0];
          const transcript = alt?.transcript;
          if (!transcript) return;

          const payload = {
            transcript,
            confidence: typeof alt.confidence === "number" ? alt.confidence : null,
            words: Array.isArray(alt.words) ? alt.words : [],
            isFinal: !!data.is_final,
            speechFinal: !!data.speech_final,
            start: typeof data.start === "number" ? data.start : null,
            duration: typeof data.duration === "number" ? data.duration : null,
          };

          if (payload.isFinal) {
            const startMs = Number.isFinite(payload.start) ? payload.start * 1000 : null;
            const endMs = startMs != null && Number.isFinite(payload.duration)
              ? startMs + payload.duration * 1000
              : null;
            payload.pauseMs = startMs != null && lastFinalEndMsRef.current != null
              ? Math.max(0, startMs - lastFinalEndMsRef.current)
              : null;
            if (endMs != null) lastFinalEndMsRef.current = endMs;
          }

          if (DEBUG_DEEPGRAM && !debugLoggedRef.current) {
            debugLoggedRef.current = true;
            console.log("[VOICE] Deepgram sample", {
              transcript: payload.transcript,
              confidence: payload.confidence,
              words: payload.words.slice(0, 4),
              isFinal: payload.isFinal,
              speechFinal: payload.speechFinal,
              start: payload.start,
              duration: payload.duration,
            });
          }

          setLastSpeechAt(Date.now());
          if (payload.isFinal) {
            onTranscript(payload);
            safeVibrate(payload.confidence != null && payload.confidence < 0.75 ? [20, 30, 20] : 15);
          } else if (onInterim) {
            onInterim(payload);
          }
        } catch (e) {
          console.warn("[VOICE] Message parse error:", e);
        }
      };

      ws.onerror = () => {
        if (intentionalCloseRef.current) return;
        setError("Error de red o conexión rechazada.");
        setStatus("error");
        setIsRecording(false);
        setLastSpeechAt(null);
        setSilenceMs(0);
        playCues("error");
        safeVibrate([40, 40, 40]);
      };

      ws.onclose = (event) => {
        if (keepAliveRef.current) {
          clearInterval(keepAliveRef.current);
          keepAliveRef.current = null;
        }
        cleanupMedia();

        if (intentionalCloseRef.current || event.code === 1000 || event.code === 1005) {
          setStatus("idle");
          setIsRecording(false);
          setLastSpeechAt(null);
          setSilenceMs(0);
          if (event.code === 1000 && !intentionalCloseRef.current) {
            playCues("stop");
            safeVibrate(10);
          } else if (intentionalCloseRef.current && event.code === 1000) {
            playCues("stop");
            safeVibrate(10);
          }
          return;
        }

        if (event.code === 4001) setError("Auth: credencial de Deepgram no válida (4001).");
        else if (event.code === 1011) setError("Servidor: timeout o error Deepgram (1011). ¿Audio llegando?");
        else if (event.code === 1008) setError("Audio no decodificable (1008). Reintentá.");
        else if (event.code === 1006) {
          setError(Date.now() - startTime < 500 ? "Conexión rechazada instantáneamente." : "Conexión perdida (1006).");
        } else {
          setError(`Conexión cerrada: ${event.code}`);
        }
        playCues("error");
        safeVibrate([40, 40, 40]);
        setStatus("error");
        setIsRecording(false);
        setLastSpeechAt(null);
        setSilenceMs(0);
      };
    } catch (err) {
      cleanupMedia();
      const userMessage = err?.name === "NotAllowedError"
        ? "Permiso de micrófono bloqueado. Permitilo en el candado del navegador y reintentá."
        : err?.name === "AbortError"
          ? "Deepgram tardó demasiado en responder. Reintentá."
          : (err?.message || "No se pudo iniciar el dictado.");
      setError(userMessage);
      setStatus("error");
      setIsRecording(false);
      safeVibrate([40, 40, 40]);
    }
  };

  const stopRecording = () => {
    intentionalCloseRef.current = true;
    lastFinalEndMsRef.current = null;
    setLastSpeechAt(null);
    setSilenceMs(0);
    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try {
        mediaRecorderRef.current.stop();
      } catch (_e) {}
    }
    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify({ type: "CloseStream" }));
        } catch (_e) {}
        wsRef.current.close(1000);
      }
      wsRef.current = null;
    }
    cleanupMedia();
    setIsRecording(false);
    setStatus("idle");
  };

  useEffect(() => {
    return () => {
      intentionalCloseRef.current = true;
      if (wsRef.current) wsRef.current.close();
      if (keepAliveRef.current) clearInterval(keepAliveRef.current);
      cleanupMedia();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isRecording) {
      setSilenceMs(0);
      return;
    }
    const id = setInterval(() => {
      if (typeof lastSpeechAt === "number") setSilenceMs(Date.now() - lastSpeechAt);
      else setSilenceMs(0);
    }, 500);
    return () => clearInterval(id);
  }, [isRecording, lastSpeechAt]);

  return { isRecording, status, error, voiceLevel, lastSpeechAt, silenceMs, isSilent, startRecording, stopRecording };
};
